import { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Trans, useTranslation } from "react-i18next";

import DialogBackdrop from "../DialogBackdrop";
import RELEASING_AutoCompleteComboBox from "components/General/AutoCompleteComboBox/RELEASING_AutoCompleteComboBox";
import { useSelector } from "react-redux";
import { getPoliciesOptions } from "js/formatting";
import FileUploader from "components/General/FileUploader";
import { uploadFiles } from "service/forms/uploadFiles";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import UploadDocumentsConfirmationModal from "./UploadDocumentsConfirmationModal";

export default function UploadDocumentsModal({ modalState, setModalState }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [policiesOption, setPoliciesOption] = useState([]);
  const [confirmationModalState, setConfirmationModalState] = useState(false);
  const [selectedPolicyId, setSelectedPolicyId] = useState({
    value: "all",
    displayAs: `${t("AutoComplate-select-your-contract")}`,
    selectedPolicyDisplayAs: `${t("AutoComplate-select-your-contract")}`,
  });
  const [selectedCategory, setSelectedCategory] = useState({
    value: "all",
  });
  const { policies } = useSelector((state) => state.data);
  const [images, setImages] = useState([]);

  useEffect(() => {
    const policySelectionArray = getPoliciesOptions(policies);
    let policiesOptions = [];
    policySelectionArray.map((policy) => {
      policiesOptions.push({
        ...policy,
        selectedPolicyDisplayAs: `${policy.prodType}: ${policy.value}`,
      });
    });

    setPoliciesOption([selectedPolicyId, ...policiesOptions]);

    if (policiesOptions.length === 1) {
      setPoliciesOption(policiesOptions[0]);
      setSelectedPolicyId(policies[0]?.oldPolicy);
    }
  }, [policies]);

  const onSubmit = async () => {
    setIsLoading(true);
    let hasErrorImages = [];
    images?.forEach(async (image) => {
      await uploadFiles(image)
        .then((response) => {
          images.splice(images.indexOf(image), 1);
        })
        .catch((error) => {
          images.splice(images.indexOf(image), 1);
          hasErrorImages.push(image);
          console.error("Something wrong.", error);
        });
      if (hasErrorImages?.length > 0 && images?.length == 0) {
        setImages();
        setIsLoading(false);
      } else if (hasErrorImages?.length === 0 && images?.length === 0) {
        setIsLoading(false);
        setModalState(false);
        setImages([]);
        setSelectedCategory("all");
        setConfirmationModalState(true);
      }
    });
  };

  return (
    <>
      <Transition appear show={modalState} as={Fragment}>
        <Dialog
          className="fixed inset-0 z-10 overflow-y-auto"
          as="div"
          open={modalState}
          onClose={() => null}
        >
          <DialogBackdrop />

          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0" />
            </Transition.Child>
            {/* This element is to trick the browser into centering the modal contents. */}

            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <Dialog.Title
                  as="h2"
                  className="dialog_label2 font-body text-xl font-bold text-left"
                >
                  {t("UploadDocumentsModal-title")}
                  <button
                    className="ui-default-dialog-close"
                    onClick={() => {
                      setModalState(false);
                      setImages([]);
                      setSelectedCategory("all");
                    }}
                  >
                    <svg
                      className="ui-icon h-3.5 w-3.5"
                      focusable="false"
                      aria-hidden="true"
                      role="presentation"
                    >
                      <use href="#ui-icon-close"></use>
                    </svg>
                  </button>
                </Dialog.Title>
                <Dialog.Title
                  as="h2"
                  className="dialog_label2 font-body text-sm text-left"
                >
                  {t("UploadDocumentsModal-sub-title1")}
                </Dialog.Title>
                <Dialog.Title
                  as="h2"
                  className="flex-grow font-body font-semibold text-xs italic text-ui-red"
                >
                  {t("UploadDocumentsModal-required-fields")}
                </Dialog.Title>
                <hr />
                <EverlakeLoadingOverlay isLoading={isLoading}>
                  <div className="grid md:grid-cols-2 pb-5 gap-5">
                    <div className="md:col-span-1">
                      <RELEASING_AutoCompleteComboBox
                        labelName="Contract Number *"
                        inputPlaceholderText={t(
                          "AutoComplate-select-your-contract"
                        )}
                        options={policiesOption}
                        selectedOption={selectedPolicyId}
                        setSelectedOption={(option) =>
                          setSelectedPolicyId(option.value)
                        }
                        readOnly={policies?.length === 1}
                      />
                    </div>
                    <div className="md:col-span-1">
                      <div>
                        <RELEASING_AutoCompleteComboBox
                          labelName={"Category *"}
                          inputPlaceholderText={t(
                            "CustomerForms-category-inputPlaceholderText"
                          )}
                          options={[
                            {
                              value: "all",
                              displayAs: t("CustomerForms-all-category"),
                              selectedPolicyDisplayAs: t(
                                "CustomerForms-all-category"
                              ),
                            },
                            {
                              value: "Beneficiary",
                              displayAs: "Beneficiary",
                              selectedPolicyDisplayAs: "Beneficiary",
                            },
                            {
                              value: "Contract",
                              displayAs: "Contract",
                              selectedPolicyDisplayAs: "Contract",
                            },
                            {
                              value: "Agent",
                              displayAs: "Agent",
                              selectedPolicyDisplayAs: "Agent",
                            },
                            {
                              value: "Billing",
                              displayAs: "Billing",
                              selectedPolicyDisplayAs: "Billing",
                            },
                            {
                              value: "DirectDeposit",
                              displayAs: "Direct Deposit",
                              selectedPolicyDisplayAs: "Direct Deposit",
                            },
                            {
                              value: "InformationAuthorization",
                              displayAs: "Information Authorization",
                              selectedPolicyDisplayAs:
                                "Information Authorization",
                            },
                            {
                              value: "Address",
                              displayAs: "Address",
                              selectedPolicyDisplayAs: "Address",
                            },
                            {
                              value: "Other",
                              displayAs: "Other",
                              selectedPolicyDisplayAs: "Other",
                            },
                          ]}
                          selectedOption={selectedCategory}
                          setSelectedOption={(option) =>
                            setSelectedCategory(option.value)
                          }
                          readOnly={false}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2">
                    <div className="md:col-span-2">
                      <FileUploader
                        images={images}
                        setImages={setImages}
                        selectedCategory={selectedCategory}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end my-4">
                    <button
                      className="ui-btn-secondary ui-btn-min-w ml-2"
                      onClick={() => {
                        setModalState(false);
                        setImages([]);
                        setSelectedCategory("all");
                      }}
                    >
                      {t("UploadDocumentsModal-cancel")}
                    </button>
                    <button
                      className="ui-btn-primary ui-btn-min-w ml-2"
                      onClick={() => {
                        onSubmit();
                      }}
                      disabled={
                        images.length === 0 ||
                        !selectedCategory ||
                        selectedCategory === "all"
                      }
                    >
                      {t("UploadDocumentsModal-submit")}
                    </button>
                  </div>
                </EverlakeLoadingOverlay>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
      <UploadDocumentsConfirmationModal
        confirmationModalState={confirmationModalState}
        setConfirmationModalState={setConfirmationModalState}
      />
    </>
  );
}
